/* eslint-disable no-plusplus */
/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useCallback, useMemo, memo } from "react";
import { BlockNoteView } from "@blocknote/mantine";
import { t } from "i18next";
import { BlockNoteSchema, defaultBlockSpecs, BlockNoteExtension } from "@blocknote/core";
import { fr } from "@blocknote/core/locales";
import { SuggestionMenuController, useCreateBlockNote } from "@blocknote/react";
import "@blocknote/mantine/style.css";
import "@blocknote/core/fonts/inter.css";
import MDBox from "components/Basics/MDBox";
import useMapDictionary from "hooks/useMapDictionary";
import MenuList from "./MentionMenu/MentionList";
import MentionCodeBox from "./Mention";
import { debounce } from "lodash";
import { Plugin, PluginKey } from "@tiptap/pm/state";

const singleLineBlockSpec = { paragraph: defaultBlockSpecs.paragraph };

const CustomSuggestionMenu = memo(
	({ items = [{ title: "" }], loadingState, onItemClick, editorBlock, dictionary }) => {
		if (loadingState?.includes("loading")) {
			return <div>{t("COMPONENT.ANSWERTYPE.BLOCKNOTE.chargement")}</div>;
		}
		if (!items.length) {
			return <div>{t("COMPONENT.ANSWERTYPE.BLOCKNOTE.itemNotfound")}</div>;
		}

		return (
			<MenuList
				items={items}
				editorBlock={editorBlock}
				onItemClick={onItemClick}
				dictionary={dictionary}
			/>
		);
	}
);
class NoNewLineExtension extends BlockNoteExtension {
	static key() {
		return "no_new_line";
	}

	constructor() {
		super();
		this.addProsemirrorPlugin(
			new Plugin({
				key: new PluginKey("eventHandler"),
				props: {
					// eslint-disable-next-line consistent-return
					handleKeyDown: (view, event) => {
						if (event.key === "Enter" && !event.shiftKey) {
							return true;
						}
					}
				}
			})
		);
	}
}

// Create an instance to use
const NoNewLine = new NoNewLineExtension();

/**
 * Advanced input (v2) using BlockNote
 * This component allows for a single line input with mentions and custom suggestions.
 * It uses BlockNote for rich text editing and mentions.
 * The component is memoized to prevent unnecessary re-renders.
 * @param {Object} props - The component props.
 * @param {Object} props.dictionary - The dictionary for the component.
 * @param {Function} props.onChange - The function to call when the input changes.
 * @param {string} props.value - The current value of the input.
 * @param {string} props.trigger - The trigger for the component.
 * @param {string} [props.placeholder=""] - The placeholder text for the input.
 * @returns {JSX.Element} The rendered component.
 */
const AdvancedInput2 = memo(
	({
		dictionary,
		onChange: onChangeProps,
		value,
		trigger,
		placeholder = "",
		enableVariables = true,
		multiline = true,
		...rest
	}) => {
		const { existingKeys } = useMapDictionary(dictionary);
		const memoizedExistingKeys = useMemo(() => existingKeys, [JSON.stringify(existingKeys)]);

		// Memoize schema creation
		const schema = useMemo(
			() =>
				BlockNoteSchema.create({
					inlineContentSpecs: { mention: MentionCodeBox },

					blockSpecs: singleLineBlockSpec
				}),
			[]
		);

		const editorBlock = useCreateBlockNote({
			extensions: multiline ? [] : [NoNewLine],
			sideMenuDetection: "never",
			schema,
			initialContent: [{ type: "paragraph" }],
			dictionary: {
				...fr,
				placeholders: {
					default: placeholder,
					heading: placeholder,
					emptyDocument: placeholder
				}
			},
			trailingBlock: false
		});

		// Memoize fetchData function
		const fetchData = useCallback(async () => {
			const newValue = value || "";
			const replacedValue = memoizedExistingKeys.reduce((acc, keyItem) => {
				return acc.replaceAll(
					`{{{ ${keyItem.variable} }}}`,
					`<span class="mention-tag" data-inline-content-type="mention" data-user="${keyItem.value}">${keyItem.value}</span>`
				);
			}, newValue);

			// Preserve empty paragraphs by adding a non-breaking space
			const preservedValue = replacedValue
				.replaceAll(/<p>\s*<\/p>/g, "<p>&nbsp;</p>")
				.replaceAll("\n", "<br>");

			// Prevent redundant updates
			const currentHtml = await editorBlock.blocksToHTMLLossy(editorBlock.document);
			if (currentHtml !== preservedValue) {
				const newBlock = await editorBlock.tryParseHTMLToBlocks(preservedValue);
				editorBlock.replaceBlocks(editorBlock.document, newBlock);
			}
			/**
			 * NOTE: Do not remmove trigger from the dependency array
			 * It's used to rerender the component when the trigger changes
			 * => Used on AI answer generation
			 */
		}, [trigger]);

		const onChange = useCallback(
			debounce(async () => {
				let htmlContent = await editorBlock.blocksToHTMLLossy(editorBlock.document);
				// Preserve empty paragraphs by adding a non-breaking space

				existingKeys.forEach(keyItem => {
					htmlContent = htmlContent.replaceAll(
						`<span class="mention-tag" data-inline-content-type="mention" data-user="${keyItem.value}">${keyItem.value}</span>`,
						`{{{ ${keyItem.variable} }}}`
					);
				});

				let preservedContent = htmlContent;

				if (multiline) {
					// Replace all </p> except the last one
					const matches = [...preservedContent.matchAll(/<\/p>/g)];
					if (matches.length > 1) {
						let replaced = preservedContent;
						// Replace all except the last
						for (let i = 0; i < matches.length - 1; i++) {
							replaced = replaced.replace("</p>", "</p>\n");
						}
						preservedContent = replaced;
					}

					preservedContent = preservedContent.replaceAll(/<br\s*\/?>/gi, "\n");
				} else {
					preservedContent = htmlContent.replaceAll(/<p>\s*<\/p>/g, "<p>&nbsp;</p>");
					if (preservedContent === "<p>&nbsp;</p>") {
						// Case where the content is empty
						preservedContent = "";
					}
				}

				const plainText = preservedContent.replaceAll(/<[^>]+>/g, "");

				onChangeProps(plainText);
			}, 300),
			[editorBlock, onChangeProps]
		);

		useEffect(() => {
			fetchData();
		}, [fetchData]);

		return (
			<MDBox>
				<BlockNoteView
					editor={editorBlock}
					onChange={onChange}
					lang="fr"
					theme="light"
					formattingToolbar={false}
					slashMenu={false}
					tableHandles={false}
					filePanel={false}
					comments={false}
					sideMenu={false}
					emojiPicker={false}
					{...rest}
				>
					{enableVariables && (
						<SuggestionMenuController
							triggerCharacter="@"
							suggestionMenuComponent={props => (
								<CustomSuggestionMenu
									{...props}
									editorBlock={editorBlock}
									dictionary={dictionary}
								/>
							)}
							onItemClick={() => {}}
						/>
					)}
				</BlockNoteView>
			</MDBox>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.trigger === nextProps.trigger &&
			prevProps.value === nextProps.value &&
			JSON.stringify(prevProps.dictionary) === JSON.stringify(nextProps.dictionary)
		);
	}
);

export default AdvancedInput2;
